import { useContext, useEffect, useMemo, useState } from 'react';
import { Button, Col, Divider, Space, Typography } from 'antd';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useKeycloak } from 'all-in-one-react-keycloak';
import { useGetDocument, ViewClaimDetailsResponse, CLAIM_STATUSES } from 'all-in-one-shared-api';

import ClaimBadge from 'pages/claims/components/ClaimCard/ClaimBadge';
import ClaimTypeIcon from 'components/claimTypeIcon';
import { type FormattedClaimDetails } from 'utils/formatClaimDetails';
// import Base64 from 'utils/Base64';
// import { setNewClaimFormInput } from 'pages/newClaim/utils';
import { useClaimPropertiesContext } from 'contexts/claimProperties';
// import { isLocaleEnglish } from 'utils/languageUtils';
import { useCachedClaimProperties } from 'hooks';
import { StyledTitle } from 'components/StyledTitle';
import { getClaimPageUrl } from 'utils';

import ClaimHistorySteps from '../ClaimHistorySteps';
import { ClaimCopyModal } from '../ClaimCopyModal';
import { DeleteClaimModal } from '../DeleteClaimModal';
import {
  BasicInfoRow,
  // BravoCopyIcon,
  BravoDeleteIcon,
  BravoPdfIcon,
  BravoSendIcon,
  QuickActionsSpace,
  RootCard,
} from './components';
import { HomeContext } from 'contexts/home/context';
import { BravoEditIcon } from '../styledComponents';

const { Text } = Typography;

export type ClaimOverviewCardProps = Pick<
  FormattedClaimDetails,
  | 'claimId'
  | 'sequenceNo'
  | 'referenceNo'
  | 'diagnosisName'
  | 'doctorName'
  | 'claimType'
  | 'claimStatus'
  | 'ctcRequest'
  | 'statusHistories'
  | 'settlementFile'
  | 'editAllowed'
  | 'claimTypeDescription'
  | 'shortfallFlag'
> & {
  claimData: Readonly<ViewClaimDetailsResponse>;
  refreshClaimData: () => void;
};

export const ClaimOverviewCard = ({
  claimId,
  sequenceNo,
  claimData,
  referenceNo,
  claimType,
  ctcRequest,
  diagnosisName,
  claimStatus,
  doctorName,
  statusHistories,
  settlementFile,
  refreshClaimData,
  editAllowed,
  claimTypeDescription,
  shortfallFlag,
}: ClaimOverviewCardProps): JSX.Element => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { initialized, keycloak } = useKeycloak();

  const { key: claimPropertiesKey } = useClaimPropertiesContext();
  const { data: claimProperties } = useCachedClaimProperties(claimPropertiesKey);

  const [returnCopyModalVisible, setReturnCopyModalVisibility] = useState(false);
  const [deleteModalVisible, setDeleteModalVisibility] = useState(false);
  const { state } = useContext(HomeContext);

  const { data: settlementFileData, shouldFetch: getSettlementFileShouldFetch } = useGetDocument(
    'getSettlementFile',
    keycloak.token ?? '',
    settlementFile ?? '',
    true,
  );

  const editUrl = getClaimPageUrl({
    claimId: claimId || referenceNo,
    claimStatus,
    sequenceNo,
    path: 'edit',
  });

  useEffect(() => {
    getSettlementFileShouldFetch(!!settlementFile && initialized && !!keycloak.authenticated && !!keycloak.token);
  }, [getSettlementFileShouldFetch, settlementFile, initialized, keycloak.authenticated, keycloak.token]);

  const quickActions: JSX.Element[] = useMemo(() => {
    const result = [];
    const handleViewClaimSettlementNoticeClick = () => {
      if (settlementFileData) {
        const settlementTag = document.createElement('a');
        settlementTag.style.display = 'none';
        settlementTag.href = settlementFileData?.presignedUrl;
        settlementTag.target = '_blank';
        settlementTag.download = settlementFileData?.keyName;
        document.body.appendChild(settlementTag);
        settlementTag.click();
        document.body.removeChild(settlementTag);
      }
    };
    // if (claimStatus !== CLAIM_STATUSES.SHORTFALL && state.userAccess?.submitClaim) {
    //   result.push(
    //     <Button
    //       key="copy"
    //       icon={<BravoCopyIcon />}
    //       onClick={() => {
    //         const queryStringData = JSON.stringify(
    //           setNewClaimFormInput(claimProperties?.claimTypes || [], isLocaleEnglish(i18n.language))(claimData),
    //         );
    //         const encodedClaimData = Base64.encode(encodeURIComponent(queryStringData));
    //         navigate(`/claims/new?data=${encodedClaimData}`);
    //       }}
    //     >
    //       <Text>{t('claimDetails.copyAsNewClaim')}</Text>
    //     </Button>,
    //   );
    // }

    if (claimStatus == CLAIM_STATUSES.SUBMITTED && editAllowed) {
      result.push(
        <Button key="submitted" icon={<BravoDeleteIcon />} onClick={() => setDeleteModalVisibility(true)}>
          <Text>{t('claimDetails.delete')}</Text>
        </Button>,
      );

      if (editUrl) {
        result.push(
          <Link key="edit" to={editUrl}>
            <Button icon={<BravoEditIcon />}>
              <Text>{t('claimDetails.edit')}</Text>
            </Button>
          </Link>,
        );
      }
    }

    if (
      [CLAIM_STATUSES.DECLINED, CLAIM_STATUSES.COMPLETED, CLAIM_STATUSES.SHORTFALL, CLAIM_STATUSES.NEED_ACTION]
        .map((status) => status.toString())
        .includes(claimStatus)
    ) {
      if (settlementFile) {
        result.push(
          <Button
            key="declinedOrCompleted"
            icon={<BravoPdfIcon />}
            onClick={() => handleViewClaimSettlementNoticeClick()}
          >
            <Text>{t('claimDetails.viewClaimSettlementNotice')}</Text>
          </Button>,
        );
      }

      if (ctcRequest) {
        result.push(
          <Button key="ctcRequest" icon={<BravoSendIcon />} onClick={() => setReturnCopyModalVisibility(true)}>
            <Text>{t('claimDetails.requestToReturnCertifiedTrueCopyReceipts')}</Text>
          </Button>,
        );
      }
    }
    return result;
    // eslint-disable-next-line
  }, [
    settlementFileData,
    editAllowed,
    claimStatus,
    ctcRequest,
    settlementFile,
    t,
    editUrl,
    claimData,
    claimProperties?.claimTypes,
    i18n.language,
    navigate,
    state.userAccess?.submitClaim,
  ]);

  return (
    <>
      <RootCard>
        <BasicInfoRow justify="space-between">
          <Col>
            <Space align="start" size="middle">
              <ClaimTypeIcon claimType={claimType} />

              <Space direction="vertical">
                <StyledTitle level={5}>{claimTypeDescription}</StyledTitle>
                <Text>{diagnosisName}</Text>
                <ClaimBadge status={shortfallFlag == 'Y' ? 'IN_PROGRESS' : claimStatus} />
                {doctorName && <Text type="secondary">{t('claims.doctor', { doctorName })}</Text>}
              </Space>
            </Space>
          </Col>

          <Col>
            <ClaimHistorySteps steps={statusHistories} />
          </Col>
        </BasicInfoRow>

        {quickActions.length > 0 && (
          <>
            <Divider />
            <QuickActionsSpace direction="horizontal">{quickActions}</QuickActionsSpace>
          </>
        )}
      </RootCard>

      {!!claimId && !!keycloak.token && (
        <ClaimCopyModal
          authToken={keycloak.token}
          claimId={claimId}
          sequenceNo={sequenceNo}
          visible={returnCopyModalVisible}
          setVisibility={setReturnCopyModalVisibility}
          onSuccess={() => {
            refreshClaimData();
          }}
        />
      )}

      {!!referenceNo && !!keycloak.token && (
        <DeleteClaimModal
          authToken={keycloak.token}
          referenceNo={referenceNo}
          visible={deleteModalVisible}
          setVisibility={setDeleteModalVisibility}
        />
      )}
    </>
  );
};
